import { Text } from '@mantine/core';
import {
	type ColumnDef,
	type ColumnHelper,
	createColumnHelper,
	type DeepKeys,
	type RowData,
} from '@tanstack/react-table';

import { Link } from '@apple/ui/link/Link';

import type { DataTableFeatures } from '../features';
import type { FilterData } from '../types';
import { createFilterOnlyColumn, type FilterOnlyColumnOptions } from './filters';

export interface DataTableColumnHelper<TData extends RowData, TFilters extends FilterData<TFilters>>
	extends ColumnHelper<DataTableFeatures, TData> {
	link: typeof createLinkColumn<TData>;
	filterOnly: <TAccessor extends DeepKeys<TData>>(
		accessor: TAccessor,
		options: Omit<FilterOnlyColumnOptions<TFilters, TAccessor & keyof TFilters>, 'field'>,
	) => ReturnType<typeof createFilterOnlyColumn<TData, TFilters>>;
	downloadOnly: typeof createDownloadOnlyColumn<TData>;
}

export function createDataTableColumnHelper<
	TData extends RowData,
	TFilters extends FilterData<TFilters>,
>(): DataTableColumnHelper<TData, TFilters> {
	const columnHelper = createColumnHelper<DataTableFeatures, TData>();

	return {
		...columnHelper,
		link: createLinkColumn<TData>,
		filterOnly: (accessor, options) =>
			createFilterOnlyColumn<TData, TFilters>({
				field: accessor as keyof TFilters & string,
				...options,
			}),
		downloadOnly: createDownloadOnlyColumn<TData>,
	};
}

function createLinkColumn<TData extends RowData>(options: {
	accessorKey: keyof TData;
	to: (row: TData) => string;
	label: string;
}): ColumnDef<DataTableFeatures, TData> {
	return {
		accessorKey: options.accessorKey,
		header: options.label,
		cell: ({ row }) => (
			<Link to={options.to(row.original)}>
				<Text size='sm'>{row.getValue(String(options.accessorKey)) as string}</Text>
			</Link>
		),
	};
}

export function createDownloadOnlyColumn<TData extends RowData>(options: {
	accessorKey: keyof TData;
	header: string;
}): ColumnDef<DataTableFeatures, TData> {
	return {
		accessorKey: options.accessorKey,
		header: options.header,
		enableColumnFilter: false,
		meta: { hideColumn: true, alwaysExport: true },
	};
}
